import { useMemo } from 'react';
import { proxy } from 'valtio';
import { subscribe } from 'valtio';
import { subscribeKey } from 'valtio/utils';
import { isUUID4 } from '@soundxyz/utils';
import {
  AUDIO_ERROR_ACTIONS,
  AUDIO_INFO_ACTIONS,
  ERROR_TYPE,
  PILLARS,
} from '@soundxyz/utils/constants/logging';
import { usePlayerTrackInfo } from '@components/AudioPlayer/usePlayerTrackInfo';
import type { PlaylistInput } from '@graphql/reactQuery';
import { logError } from '@hooks/useLogErrorNew';
import { logInfo } from '@hooks/useLogInfo';
import {
  endActiveSession,
  startSession,
  userPaused,
  userResumedListening,
} from '@store/analytics/playSession';
import {
  AudioEngineHTML5,
  audioEngineInstance,
  audioEngineSeek,
  audioEngineSeekBy,
  load,
  onEnd,
  onPause,
  onPlay,
  pause,
  play,
  unload,
  useAudioEngineHTML5,
} from './AudioEngineHTML5';
import { addPlaylistAudioToQueue, addTrackAudioToQueue, queryAudioFromTrack } from './AudioFetcher';
import {
  AudioMeta,
  RepeatMode,
  setActiveTrackId,
  setLoadingActiveTrack,
  setRepeatMode,
  useAudioMeta,
} from './AudioMeta';
import { setPosition } from './AudioPosition';
import {
  appendUpNextTrackIds,
  AudioQueue,
  goToNextTrack,
  goToPrevTrack,
  setActiveTrack as setActiveTrackIndex,
  setPlaylistInput,
  useAudioQueue,
} from './AudioQueue';
import {
  setMetadata,
  setPauseActionHandler,
  setPlayActionHandler,
  setPlaybackState,
  setSeekActionHandler,
  setSeekBackwardActionHandler,
  setSeekForwardActionHandler,
} from './MediaSession';
import { PersistentAudio, usePersistentAudio } from './Persistence';

const pillar = PILLARS.AUDIO;
const isArtistOnlyPage = false;
const FATAL_FETCH_ERROR = 'Cannot return null for non-nullable field Track.release';

const onLoopSkippedSongs = new Set<string>();

/**
 * Fetches audio info for a given track, prepares audio engine with the track, and sets state accordingly
 */
export const loadTrack = async ({
  autoplay = true,
  trackId,
  audioSrc,
  playlist,
  addToQueue = true,
}: {
  autoplay?: boolean;
  trackId?: string | null;
  audioSrc?: string;
  playlist?: PlaylistInput | null;
  addToQueue?: boolean;
}) => {
  // If track is already playing, do nothing
  if (!audioSrc && AudioMeta.activeTrackId && trackId === AudioMeta.activeTrackId) {
    return;
  }

  const hasInitialActiveTrack = !!AudioMeta.activeTrackId;

  // If a legacy playlist input is passed in (to generate up next for the home page, artist pages etc) set the tracks to be up next
  if (
    playlist &&
    playlist.type !== AudioQueue.playlistInput?.type &&
    playlist.associationId !== AudioQueue.playlistInput?.associationId
  ) {
    setPlaylistInput(playlist);
    addPlaylistAudioToQueue(playlist);
  }

  setLoadingActiveTrack(true);
  let src: string | undefined;

  if (audioSrc) {
    src = audioSrc;
    setActiveTrackId(null);
  } else if (
    trackId &&
    isUUID4(trackId) &&
    /**
     * If the track is on the set of temporarily skipped songs, we should stop the loop
     */
    !onLoopSkippedSongs.has(trackId)
  ) {
    // Play as soon as possible on first load so mobile recognizes the play command before load is finished
    if (autoplay && !hasInitialActiveTrack) {
      play();
    }

    let audioInfo: Awaited<ReturnType<typeof queryAudioFromTrack>>;
    try {
      logInfo({
        action: AUDIO_INFO_ACTIONS.AUDIO_FETCH_DATA,
        message: 'Fetching audio data for track',
        isArtistOnlyPage,
        pillar,
        data: {
          trackId,
          autoplay,
        },
      });
      audioInfo = await queryAudioFromTrack({ trackId });
    } catch (err) {
      logError({
        action: AUDIO_ERROR_ACTIONS.AUDIO_FETCH_ERROR,
        error: err,
        level: 'warning',
        message: 'Error fetching track audio data',
        errorType: ERROR_TYPE.MISSING_AUDIO_DATA,
        isArtistOnlyPage,
        pillar,
        indexedTags: {
          trackId,
        },
      });

      toNextTrack();
      setLoadingActiveTrack(false);
      return;
    }

    if (audioInfo?.errors) {
      const isErrorFatal = !!audioInfo.errors.find(error =>
        error.message.startsWith(FATAL_FETCH_ERROR),
      );
      const message = 'Missing audio data';
      logError({
        action: AUDIO_ERROR_ACTIONS.AUDIO_FETCH_ERROR,
        error: new Error(message),
        level: isErrorFatal ? 'fatal' : 'warning',
        message: 'Error fetching track audio data',
        errorType: ERROR_TYPE.MISSING_AUDIO_DATA,
        isArtistOnlyPage,
        pillar,
        indexedTags: {
          trackId,
        },
        unindexedExtra: {
          errors: audioInfo.errors,
        },
      });

      /**
       * If audio is not playable, skip the track and add it to the set of skipped songs to prevent infinite looping
       */
      onLoopSkippedSongs.add(trackId);

      toNextTrack();
      setLoadingActiveTrack(false);
      return;
    } else if (!audioInfo) {
      /**
       * If audio is not playable, skip the track and add it to the set of skipped songs to prevent infinite looping
       */
      onLoopSkippedSongs.add(trackId);

      toNextTrack();
      setLoadingActiveTrack(false);
      return;
    } else {
      /**
       * If any track was found playable, clear the set of skipped songs
       */
      onLoopSkippedSongs.clear();
    }

    const { url, originalUrl } = audioInfo;
    src = url ?? originalUrl;

    if (src && addToQueue) {
      addTrackAudioToQueue({ trackId });
      setActiveTrackIndex((AudioQueue.active?.playbackIndex ?? 0) + AudioQueue.queueLength);
    }
  }

  if (src) {
    load({ src, autoplay });
    if (autoplay && (hasInitialActiveTrack || audioSrc)) play();

    // Track the current play session and related stats
    endActiveSession();
    if (trackId) {
      startSession({
        startTime: Date.now(),
        trackID: trackId,
      });

      setActiveTrackId(trackId);
    }
  } else {
    pause();
  }
  setLoadingActiveTrack(false);
};

/**
 * Removes existing track data and sets playback back at last active position
 */
export const resetActiveTrack = () => {
  setActiveTrackId(null);
  unload();
  if (!AudioQueue.active) return;
  loadTrack({ trackId: AudioQueue.active.trackId, addToQueue: false, autoplay: false });
};

/**
 * Get next N track ID for consumers to use to prefetch
 */
export const getNextTracksForPrefetch = (prefetchBufferLength: number) => {
  if (!AudioQueue.active) return [];
  const index = AudioQueue.active?.playbackIndex;
  return AudioQueue.playbackTrackIds.slice(index + 1, index + 1 + prefetchBufferLength);
};

/**
 *  Loads and starts playing the active track
 */
export const loadActiveTrack = () => {
  if (!AudioQueue.active) return;

  if (AudioQueue.active.trackId === AudioMeta.activeTrackId) {
    seek(0);
    play();
  } else {
    loadTrack({ trackId: AudioQueue.active.trackId, addToQueue: false });
  }
};

/**
 * Called to go to the previous track
 */
export const toPrevTrack = () => {
  if (AudioQueue.active?.playbackIndex === 0) {
    seek(0);
    return;
  }

  goToPrevTrack();
  loadActiveTrack();
};

/**
 * Called to go to the next track
 */
export const toNextTrack = () => {
  goToNextTrack();
  loadActiveTrack();

  if (AudioMeta.repeatMode === RepeatMode.REPEAT_ONE) {
    setRepeatMode(RepeatMode.REPEAT_ALL);
  }
};

/**
 * Seek the audio to the given time in `seconds`
 */
export const seek = (value: number) => {
  audioEngineSeek(value);
  setPosition(audioEngineInstance.position);
};

/**
 * Seek the audio by the given time in `seconds`
 */
export const seekBy = (value: number) => {
  audioEngineSeekBy(value);
  setPosition(audioEngineInstance.position);
};

/**
 * Helper to determine if the last track in the queue is the currently active one
 */
const isLastTrackActive = () =>
  AudioQueue.active?.playbackIndex === AudioQueue.playbackTrackIds.length - 1;

/**
 * Used in React components to read data about the current state of audio
 */
export const useAudioController = () => {
  const {
    activePlaylistId,
    activeTrackId,
    repeatMode,
    loadingActiveTrack,
    hideAudioPlayer,
    disableNextPrev,
    playbackId,
    hideExpandedPlayer,
    activePlaylistReleaseIds,
  } = useAudioMeta();
  const { loading, muted, playing, ready, volume, duration } = useAudioEngineHTML5();
  const { playbackTrackIds, shuffleEnabled, active, queueLength } = useAudioQueue();
  const { isReady } = usePersistentAudio();

  const activeTrackIndex = active?.playbackIndex ?? 0;
  const queueTrackIds = useMemo(
    () =>
      queueLength === 0
        ? []
        : playbackTrackIds.slice(activeTrackIndex + 1, activeTrackIndex + queueLength + 1),
    [activeTrackIndex, playbackTrackIds, queueLength],
  );
  const upNextTrackIds = useMemo(
    () => playbackTrackIds.slice(activeTrackIndex + queueLength + 1),
    [activeTrackIndex, playbackTrackIds, queueLength],
  );

  const { trackAudioUrl, releaseInfo: activeTrackReleaseInfo } = usePlayerTrackInfo({
    trackId: activeTrackId,
    shouldFetch: true,
  });

  return {
    activePlaylistId,
    activePlaylistReleaseIds,
    activeTrackId,
    activeTrackIndex,
    disableNextPrev,
    duration,
    hideAudioPlayer,
    hideExpandedPlayer,
    isNextTrackDisabled: isLastTrackActive(),
    isReady,
    loading,
    loadingActiveTrack,
    muted,
    playbackId,
    playbackTrackIds,
    playing,
    queueTrackIds,
    ready,
    repeatMode,
    shuffleEnabled,
    upNextTrackIds,
    volume,
    trackAudioUrl,
    activeTrackReleaseInfo,
  };
};

if (typeof window !== 'undefined') {
  /**
   * Go to next track when current one ends depending on repeat mode
   */
  onEnd(
    () => {
      if (
        !AudioMeta.playNextTrackOnEnd ||
        (isLastTrackActive() && AudioMeta.repeatMode === RepeatMode.NO_REPEAT)
      ) {
        seek(0);
        pause();
        return;
      }

      if (AudioMeta.repeatMode === RepeatMode.REPEAT_ONE) {
        seek(0);
        play({ forcePlay: true });
        return;
      }

      toNextTrack();
    },
    { once: false },
  );

  /**
   * Playing last track when repeat all is set should add the existing up next tracks to the queue
   */
  let prevIsLastTrackActive = false;
  let prevIsRepeatAllActive = false;
  subscribe(proxy({ audioQueue: AudioQueue, audioMeta: AudioMeta }), () => {
    const currentIsLastTrackActive = isLastTrackActive();
    const currentIsRepeatAllActive = AudioMeta.repeatMode === RepeatMode.REPEAT_ALL;

    if (
      (!prevIsLastTrackActive && currentIsLastTrackActive && currentIsRepeatAllActive) ||
      (!prevIsRepeatAllActive && currentIsRepeatAllActive && currentIsLastTrackActive)
    ) {
      appendUpNextTrackIds();
    }

    prevIsLastTrackActive = currentIsLastTrackActive;
    prevIsRepeatAllActive = currentIsRepeatAllActive;
  });

  /**
   * Load active track from persistent client side storage
   */
  subscribeKey(PersistentAudio, 'isReady', () => {
    // If a track is already playing, avoid loading the persisted track
    if (
      !PersistentAudio.isReady ||
      !AudioQueue.active ||
      !!AudioMeta.activeTrackId ||
      !!AudioEngineHTML5.playing
    )
      return;

    loadTrack({
      trackId: AudioQueue.active.trackId,
      autoplay: false,
      addToQueue: false,
    });

    // Sync saved engine values with audio instance
    if (AudioEngineHTML5.instance) {
      AudioEngineHTML5.instance.muted = AudioEngineHTML5.muted;
      AudioEngineHTML5.instance.volume = AudioEngineHTML5.volume;
    }
  });

  /**
   * Update play stats when users pause the track
   */
  let offPlay: () => void | undefined;
  let offPause: () => void | undefined;
  subscribeKey(AudioMeta, 'activeTrackId', () => {
    if (offPlay) offPlay();
    if (offPause) offPause();
    let hasPaused = false;

    if (AudioMeta.activeTrackId) {
      offPause = onPause(
        () => {
          userPaused();
          hasPaused = true;
        },
        { once: false },
      );
      offPlay = onPlay(
        () => {
          if (hasPaused) {
            userResumedListening();
          }
        },
        { once: false },
      );
    }
  });

  /**
   * Attach media session handlers
   */
  setPlayActionHandler(play);
  setPauseActionHandler(pause);
  setSeekActionHandler(seek);
  setSeekBackwardActionHandler(seekBy);
  setSeekForwardActionHandler(seekBy);

  /**
   * Update media session playback state to sync with audio player
   */
  subscribeKey(AudioEngineHTML5, 'playing', async () => {
    setPlaybackState(AudioEngineHTML5.playing);
  });

  /**
   * Update MediaSession meta data when songs change
   */
  subscribeKey(AudioMeta, 'activeTrackId', async () => {
    if (!AudioMeta.activeTrackId || !isUUID4(AudioMeta.activeTrackId)) return;

    const songInfo = (
      await queryAudioFromTrack({
        trackId: AudioMeta.activeTrackId,
      })
    )?.release;

    const coverImageUrl = songInfo?.coverImage.url ?? '';
    const staticCoverImageUrl = songInfo?.staticCoverImage?.url;
    const artistName = songInfo?.artist.name ?? 'Unnamed Artist';
    const songTitle = songInfo?.title ?? 'Unnamed Song';

    // Devices check for compatibility and set the current metadata for the media session.
    // Source: https://developer.mozilla.org/en-US/docs/Web/API/MediaMetadata/artwork
    setMetadata({
      title: songTitle,
      artist: artistName,
      artwork: [
        // staticCoverImageUrl is undefined if cover image is not animated
        ...(staticCoverImageUrl
          ? [{ src: staticCoverImageUrl, sizes: '398×398', type: 'image/png' }]
          : []),
        // Hint the user agent about the type of image being used. In theory, this image could be jpeg, jpg or png.
        // We use jpeg because it is the most common image type used for album art, and it avoids some devices from
        // skipping this type and not showing an image at all
        // MDN: The MIME type hint for the user agent that allows it to ignore images of types
        // that it doesn't support. However, the user agent may still use MIME type sniffing
        // after downloading the image to determine its type.
        // Source: https://developer.mozilla.org/en-US/docs/Web/API/MediaImage
        { src: coverImageUrl, type: 'image/jpeg' },
      ],
    });
  });
}
